import React, { useState, useCallback, useMemo } from "react"
import Typography from "aldoo-ra/Typography"
import Button from "aldoo-ra/Button"
import DataTable from "aldoo-ra/DataTable"
import Checkbox from "aldoo-ra/Checkbox"
import Tooltip from "aldoo-ra/Tooltip"
import { showNotification } from "aldoo-ra/Notification"
import { showPromptDialog } from "aldoo-ra/PromptDialog"
import {
  UserMinusIcon,
  UserPlusIcon,
  StarIcon,
  TrashIcon,
  UsersIcon,
} from "@heroicons/react/24/outline"
import {
  RemoveUserFromLicense,
  UpdateLicense,
  GetLicenseUsers,
  RemoveUsersFromLicense,
} from "../../api"
import AddUserDialog from "../components/AddUserDialog"

export default function UsersTab({ license, refreshKey, setRefreshKey }) {
  const [showAddUserDialog, setShowAddUserDialog] = useState(false)
  const [selectedUsers, setSelectedUsers] = useState([])

  // Create stable API method reference and request fields
  const requestFields = useMemo(() => ({
    licenseId: license._id
  }), [license._id])

  // Create stable callback for data loading
  const handleDataLoaded = useCallback((data) => {
    return data
  }, [])

  // Handle removing user from license
  const handleRemoveUser = (user, isOwner = false) => {
    const action = isOwner ? "remove owner" : "remove user"

    showPromptDialog({
      title: `Remove ${isOwner ? "Owner" : "User"}`,
      message: `Are you sure you want to ${action} "${user.name || user.email}" from this license?${isOwner ? " This will remove ownership and may affect license status." : ""} All devices registered by this user will also be removed from the license.`,
      confirmText: "Remove",
      confirmVariant: "warning",
      onConfirm: async () => {
        try {
          const response = await RemoveUserFromLicense({
            licenseId: license._id,
            userId: user._id,
          })

          if (response.result) {
            showNotification({
              message: `${isOwner ? "Owner" : "User"} removed successfully`,
              className: "bg-admin_primary text-white",
            })
            setRefreshKey(prev => prev + 1)
          } else {
            showNotification({
              message: response.message || response.error || `Failed to ${action}`,
              className: "bg-admin_error text-white",
            })
          }
        } catch (error) {
          // Extract error message from different possible sources
          let errorMessage = `An error occurred while ${action}ing`

          if (error.response && error.response.data) {
            const responseData = error.response.data
            errorMessage = responseData.message || responseData.error || error.message
          } else if (error.message) {
            errorMessage = error.message
          }

          showNotification({
            message: errorMessage,
            className: "bg-admin_error text-white",
          })
        }
      },
    })
  }

  // Handle making user primary
  const handleMakePrimary = (user) => {
    showPromptDialog({
      title: "Make Primary Owner",
      message: `Are you sure you want to make "${user.name || user.email}" the primary owner of this license? This will transfer ownership and management privileges.`,
      confirmText: "Make Primary",
      confirmVariant: "primary",
      onConfirm: async () => {
        try {
          // Use UpdateLicense to transfer ownership by updating the ownerId field
          const response = await UpdateLicense({
            licenseId: license._id,
            ownerId: user._id
          })

          if (response.result) {
            showNotification({
              message: `${user.name || user.email} is now the primary owner`,
              className: "bg-admin_primary text-white",
            })
            setRefreshKey(prev => prev + 1)
          } else {
            showNotification({
              message: response.message || response.error || "Failed to make user primary",
              className: "bg-admin_error text-white",
            })
          }
        } catch (error) {
          // Extract error message from different possible sources
          let errorMessage = "An error occurred while making user primary"

          if (error.response && error.response.data) {
            const responseData = error.response.data
            errorMessage = responseData.message || responseData.error || error.message
          } else if (error.message) {
            errorMessage = error.message
          }

          showNotification({
            message: errorMessage,
            className: "bg-admin_error text-white",
          })
        }
      },
    })
  }

  // Handle bulk user removal
  const handleBulkRemoveUsers = () => {
    if (selectedUsers.length === 0) return

    const userCount = selectedUsers.length
    const userText = userCount === 1 ? 'user' : 'users'

    showPromptDialog({
      title: `Remove ${userCount} ${userText}`,
      message: `Are you sure you want to remove ${userCount} ${userText} from this license? All devices registered by these users will also be removed from the license.`,
      confirmText: `Remove ${userCount} ${userText}`,
      confirmVariant: "warning",
      onConfirm: async () => {
        try {
          // Use bulk removal API
          const response = await RemoveUsersFromLicense({
            licenseId: license._id,
            userIds: selectedUsers,
          })

          if (response.result && response.data) {
            const { successCount, errorCount, errors, message } = response.data

            // Show success notification with backend message
            if (successCount > 0) {
              showNotification({
                message: message || `${successCount} ${successCount === 1 ? 'user' : 'users'} removed successfully`,
                className: "bg-admin_primary text-white",
              })
            }

            // Log specific errors for debugging
            if (errors && errors.length > 0) {
              console.error('User removal errors:', errors)
            }

            // Show error notification if all failed
            if (errorCount > 0 && successCount === 0) {
              showNotification({
                message: `Failed to remove all ${errorCount} ${errorCount === 1 ? 'user' : 'users'}`,
                className: "bg-admin_error text-white",
              })
            }

            // Clear selection and refresh data
            setSelectedUsers([])
            setRefreshKey(prev => prev + 1)
          } else {
            // Handle API error response
            showNotification({
              message: response.message || response.error || "Failed to remove users",
              className: "bg-admin_error text-white",
            })
          }
        } catch (error) {
          console.error('Bulk user removal error:', error)
          showNotification({
            message: "Error during bulk user removal",
            className: "bg-admin_error text-white",
          })
        }
      },
    })
  }

  // Table footer content - action buttons
  const tableFooter = selectedUsers.length > 0 && (
    <div className="flex justify-end items-center mt-4 p-3 bg-admin_canvas rounded">
      <Typography  
        variant="admin_body" 
        className="mr-4"
      >
        {selectedUsers.length} user{selectedUsers.length === 1 ? "" : "s"} selected
      </Typography>

      <Button
        variant="admin_secondary"
        className="mr-3"
        onClick={() => setSelectedUsers([])}
      >
        Deselect
      </Button>

      <Button 
        variant="admin_error" 
        onClick={() => handleBulkRemoveUsers()}
      >
        <TrashIcon className="w-5 h-5 mr-2" />
        Remove
      </Button>
    </div>
  )

  return (
    <div className="space-y-0">
      {/* Users DataTable */}
      <div className="bg-admin_paper rounded-xl p-6">
        <DataTable
          localUserPreferencesKey="admin.licensing.viewer.users"
          variant="admin_browser"
          title="License Users"
          apiMethod={GetLicenseUsers}
          requestFields={requestFields}
          refreshKey={refreshKey}
          searchField="name email"
          itemsPerPage={10}
          headerControls={
            <Button
              variant="admin_primary"
              className="mt-5"
              onClick={() => setShowAddUserDialog(true)}
            >
              <UserPlusIcon className="w-5 h-5 mr-2" />
              Add
            </Button>
          }
          tableFooter={tableFooter}
          styleOverrides={{
            table: "w-full table-fixed",
            getRowStyle: (index, item) => {
              const isSelected = selectedUsers.includes(item._id)
              let style = index % 2 === 0 ? "bg-admin_paper" : "bg-admin_canvas"
              
              if (isSelected) {
                style = "bg-admin_primary/10 border-l-4 border-admin_primary"
              }
              return style
            }
          }}
          renders={[
            {
              id: "select",
              headerClassName: "w-8",
              header: (_, items) => (
                <Checkbox
                  variant="admin_compact"
                  isOn={items.length > 0 && selectedUsers.length === items.length}
                  onChange={(isSelected) => {
                    if (isSelected) {
                      setSelectedUsers(items.map((item) => item._id))
                    } else {
                      setSelectedUsers([])
                    }
                  }}
                />
              ),
              render: (_, item) => (
                <Checkbox
                  variant="admin_compact"
                  isOn={selectedUsers.includes(item._id)}
                  onChange={(isSelected) => {
                    if (isSelected) {
                      setSelectedUsers((prev) => [...prev, item._id])
                    } else {
                      setSelectedUsers((prev) => prev.filter((id) => id !== item._id))
                    }
                  }}
                />
              ),
            },
            {
              id: "user",
              title: "User",
              headerClassName: "w-1/3 xl:w-1/2",
              render: (_, user) => {
                const isPrimaryOwner = user.isOwner || license.owner?._id === user._id
                return (
                  <div className="w-full flex items-center space-x-3">
                    {/* User Avatar */}
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                      isPrimaryOwner ? 'bg-admin_primary' : 'bg-admin_text_secondary'
                    }`}>
                      <Typography 
                        variant="admin_txt2" 
                        className="text-white font-medium"
                      >
                        {(user.name || user.email).charAt(0).toUpperCase()}
                      </Typography>
                    </div>

                    {/* User Name and Email */}
                    <div className="min-w-0 flex-1">
                      <Typography 
                        variant="admin_body" 
                        className="font-medium my-0"
                      >
                        {user.name || user.email}
                      </Typography>

                      {user.name && user.email && (
                        <Tooltip 
                          display="block"
                          content={
                            <div className="text-center">
                              <Typography 
                                variant="admin_txt1"
                                className="block"
                              >
                                {user?.email || "No email"}
                              </Typography>
            
                              <Typography variant="admin_txt2_dim">
                                Click to copy
                              </Typography>
                            </div>
                          }
                        >
                          <button 
                            onClick={async () => {
                              try {
                                await navigator.clipboard.writeText(user?.email)
                                showNotification({
                                  message: "Copied to clipboard",
                                  className: "bg-admin_primary text-white"
                                })
                              } catch (error) {
                                console.error("Failed to copy:", error)
                              }
                            }}
                            className="w-full truncate whitespace-nowrap text-admin_text_secondary hover:text-admin_primary transition-colors text-left"
                          >
                            {user.email}
                          </button>
                        </Tooltip>
                      )}
                    </div>
                  </div>
                )
              },
            },
            {
              id: "role",
              title: "Role",
              headerClassName: "w-[220px]",
              render: (_, user) => {
                const isPrimaryOwner = user.isOwner || license.owner?._id === user._id
                return (
                  <div className="w-full flex items-center space-x-2">
                    {isPrimaryOwner ? (
                      <span className="block bg-admin_primary text-white text-xs rounded-full whitespace-nowrap px-2 py-1">
                        Primary Owner
                      </span>
                    ) : (
                      <>
                        <span className="px-2 py-1 bg-admin_text_secondary text-white text-xs rounded-full">
                          Secondary User
                        </span>

                        <button
                          className="px-2 py-1 bg-admin_primary/10 text-admin_primary hover:bg-admin_primary hover:text-white text-xs rounded-full transition-colors flex items-center space-x-1 whitespace-nowrap"
                          onClick={() => handleMakePrimary(user)}
                          title="Make this user the primary owner"
                        >
                          <StarIcon className="w-3 h-3" />
                          <span>Make Primary</span>
                        </button>
                      </>
                    )}
                  </div>
                )
              },
            },
            {
              id: "actions",
              title: "Actions",
              headerClassName: "w-[140px]",
              render: (_, user) => {
                const isPrimaryOwner = user.isOwner || license.owner?._id === user._id
                return (
                  <Button
                    variant="admin_secondary"
                    className="whitespace-nowrap"
                    onClick={() => handleRemoveUser(user, isPrimaryOwner)}
                  >
                    <UserMinusIcon className="w-5 h-5 mr-2" />
                    {isPrimaryOwner ? 'Remove Owner' : 'Remove'}
                  </Button>
                )
              },
            },
          ]}
          noData={
            <div className="text-center py-8">
              <UsersIcon className="w-12 h-12 text-admin_text_secondary mx-auto mb-4" />

              <Typography 
                variant="admin_body_dim">
                No users assigned to this license
              </Typography>

              <Typography variant="admin_txt1_dim">
                Click "Add User" to assign users who can activate this license.
              </Typography>
            </div>
          }
          onDataLoaded={handleDataLoaded}
        />

        {/* Bulk actions for selected users */}
        {/* {selectedUsers.length > 0 && (
          <div className="flex justify-end items-center mt-4 p-3 bg-admin_canvas rounded">
            <Typography variant="admin_body" className="mr-4">
              {selectedUsers.length} user{selectedUsers.length === 1 ? "" : "s"} selected
            </Typography>

            <Button
              variant="admin_secondary"
              className="mr-3"
              onClick={() => setSelectedUsers([])}
            >
              Clear Selection
            </Button>

            <Button variant="admin_error" onClick={() => handleBulkRemoveUsers()}>
              <TrashIcon className="w-5 h-5 mr-2" />
              Remove Selected
            </Button>
          </div>
        )} */}

        {/* User Management Info */}
        <div className="mt-6 p-4 bg-admin_canvas rounded-lg">
          <Typography 
            component="h2"
            variant="admin_h4" 
            className="mb-4"
          >
            User Management
          </Typography>

          <ul className="space-y-3 list-disc pl-5 text-admin_text">
            <li>
              <strong>Primary Owner:</strong> Has full control over the license and can manage devices
            </li>

            <li>
              <strong>Secondary Users:</strong> Can activate devices up to the seat limit, but cannot manage other users
            </li>

            <li>
              <strong>Removing users</strong> will also remove their registered devices from the license
            </li>
          </ul>
        </div>
      </div>

      {/* Add User Dialog */}
      <AddUserDialog
        isOpen={showAddUserDialog}
        onClose={() => setShowAddUserDialog(false)}
        license={license}
        setRefreshKey={setRefreshKey}
      />
    </div>
  )
}